const libFableService = require('fable-serviceproviderbase');
const libPictSectionModal = require('pict-section-modal');
const libHeadlightModalAdapter = require('./HeadlightModalAdapter.js');

/**
 * HeadlightModal
 *
 * Confirmation / dialog / loader broker for Headlight. This used to hand-build Bulma
 * `.modal` / `.modal-card` DOM; it now drives the pict-section-modal (PSM) view's
 * `show()` API, which gives the shared overlay, Escape/focus/ARIA, transitions and
 * CSS-variable theming for free.
 *
 * The public surface (spawnModal / spawnMiniModal / addLoader / dismissLoader /
 * dismissModals / freezeBodyScroll / unfreezeBodyScroll, plus the mutable
 * confirmCallback / cancelCallback slots) is preserved so the ~25 call sites and the
 * `_Pict.fable.HeadlightModal.*()` onclick strings embedded in their template HTML keep
 * working unchanged.
 *
 * Option translation lives in the pure `HeadlightModalAdapter`; everything impure — the
 * PSM call, live-DOM append + autofocus + `<style>` injection in onOpen, the
 * callback<->dismiss bridge, the body scroll-lock, and the singleton dismiss — lives here.
 */
class HeadlightModalFableService extends libFableService
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/**
		 * @type {import('fable') & { ContentAssignment: import('pict/types/source/Pict-Content-Assignment'), views: object, CSSMap: object }}
		 */
		this.fable;
		// store this for the "jump to top" issue that may occur when modals are dismissed
		this.lastScrollPosition = 0;

		this.serviceType = 'HeadlightModal';

		this.loaderActive = false;

		// Pure legacy-options -> PSM-options translator (no DOM / Fable deps).
		this._adapter = new libHeadlightModalAdapter();
		// The panel-close "MiniModal" preset, exposed on the instance so views can spread it:
		//   spawnMiniModal({ ...this.fable.HeadlightModal.MiniModalPreset, message: '...' }, cb)
		this.MiniModalPreset = libHeadlightModalAdapter.MiniModalPreset;
		// The live PSM dialog element for the current modal (singleton), or null.
		this._activeDialog = null;

		// default configuration for the modal
		this.configDefault = {
			modalId: 'Headlight-Utility-Modal',
			modalBodyId: 'Headlight-Utility-Modal-Body',
			modalHeaderId: 'Headlight-Utility-Modal-Header',
			modalFooterId: 'Headlight-Utility-Modal-Footer',
			modalCardTemplate: null,
			modalHeaderTemplate: null,
			modalBodyTemplate: null,
			modalFooterTemplate: null,
			modalCustomCSS: null,
			hideModalBackground: false,
			hideModalHeader: false,
			hideModalBody: false,
			hideModalFooter: false,
			title: 'Confirmation',
			message: 'Are you sure?',
			confirmText: 'Okay',
			confirmIcon: 'check',
			confirmColor: 'is-link',
			cancelText: 'Cancel',
			cancelIcon: 'times',
			cancelColor: 'is-link is-soft',
			style: 'small',
			enableBackgroundScrolling: false,
			disableBackgroundClickToClose: false,
		};
		this.configActive = Object.assign({}, this.configDefault);
		// default callbacks for the modal
		this.confirmCallback = () =>
		{
			this.dismissModals();
		};
		this.cancelCallback = () =>
		{
			this.dismissModals();
		};
	}

	/**
	 * Resolve the pict-section-modal view, registering it as a singleton on first use if
	 * the host app hasn't already. Tolerates both registration names in use (canonical
	 * 'Pict-Section-Modal', and the form-editor's 'PictSectionModal'). Mirrors
	 * Headlight-Popover-Service so the modal stays a drop-in service.
	 *
	 * @returns {Object|false} The PSM view instance, or false if unavailable.
	 */
	_getModal()
	{
		if (!this.fable || !this.fable.views)
		{
			this.fable.log.error('HeadlightModalFableService: pict-section-modal is unavailable (no views registry)');
			return false;
		}
		let tmpModal = this.fable.views['Pict-Section-Modal'] || this.fable.views['PictSectionModal'];
		if (!tmpModal && typeof this.fable.addViewSingleton === 'function')
		{
			this.fable.addViewSingleton('Pict-Section-Modal', libPictSectionModal.default_configuration, libPictSectionModal);
			tmpModal = this.fable.views['Pict-Section-Modal'];
		}
		if (!tmpModal)
		{
			this.fable.log.error('HeadlightModalFableService: could not resolve the Pict-Section-Modal view');
			return false;
		}
		return tmpModal;
	}

	/**
	 * Generic modal creation utility. Signature preserved from the legacy service; the
	 * options object is translated to a PSM `show()` call. See HeadlightModalAdapter for
	 * the option mapping.
	 *
	 * @param {object} configOptions - Behaviors/parts of the modal to customize/override.
	 * @param {function} [confirmCallback] - Called on confirm.
	 * @param {function} [cancelCallback] - Called on cancel.
	 * @return {void}
	 */
	spawnModal(configOptions = {}, confirmCallback, cancelCallback)
	{
		this._spawn(Object.assign({}, this.configDefault, configOptions), confirmCallback, cancelCallback, { mini: false });
	}

	/**
	 * Mini modal creation utility — the compact panel-close confirmation. Signature
	 * preserved. NOTE: the legacy first-arg-is-a-function misuse (three call sites passed
	 * `spawnMiniModal(() => {...})`) is normalized here so the confirm action fires.
	 *
	 * @param {object|function} pConfigOptions
	 * @param {function} [confirmCallback]
	 * @param {function} [cancelCallback]
	 * @return {void}
	 */
	spawnMiniModal(pConfigOptions = {}, confirmCallback, cancelCallback)
	{
		// Legacy bug fix: a function passed as the first arg is really the confirm callback.
		if (typeof pConfigOptions === 'function')
		{
			cancelCallback = confirmCallback;
			confirmCallback = pConfigOptions;
			pConfigOptions = {};
		}
		if (!pConfigOptions)
		{
			pConfigOptions = {};
		}

		// mini structural defaults (the brand + offset come from the caller / MiniModalPreset)
		pConfigOptions.message = pConfigOptions.message || 'Close panel?';
		pConfigOptions.subtext = pConfigOptions.subtext || 'Any unsaved changes may be lost.';
		pConfigOptions.confirmText = pConfigOptions.confirmText || 'Confirm';
		pConfigOptions.confirmIcon = pConfigOptions.confirmIcon || 'fa-check-circle';
		pConfigOptions.confirmColor = pConfigOptions.confirmColor || '';
		pConfigOptions.cancelText = pConfigOptions.cancelText || 'Cancel';
		pConfigOptions.cancelIcon = pConfigOptions.cancelIcon || 'fa-times-circle';
		pConfigOptions.cancelColor = (pConfigOptions.cancelColor !== undefined && pConfigOptions.cancelColor !== null) ? pConfigOptions.cancelColor : 'is-light';
		pConfigOptions.disableBackgroundClickToClose = true;
		// The mini is a compact colored card — no title/header bar (parity with the legacy
		// .modal-card-mini dropdown, which had none).
		if (pConfigOptions.hideModalHeader === undefined || pConfigOptions.hideModalHeader === null)
		{
			pConfigOptions.hideModalHeader = true;
		}

		this._spawn(Object.assign({}, this.configDefault, pConfigOptions), confirmCallback, cancelCallback, { mini: true });
	}

	/**
	 * Anchored, backdrop-less menu (e.g. the navbar download menu). Wraps PSM's
	 * `dropdown()` in ContentHTML mode, which gives anchor positioning, auto-flip,
	 * click-outside / Escape dismissal and viewport clamping — replacing the old
	 * `spawnModal({ modalCardTemplate, hideModalBackground })` + hand-rolled
	 * getBoundingClientRect positioning + document click-outside closer.
	 *
	 * @param {HTMLElement|string|object} pAnchor - element, selector, or rect
	 * @param {string} pContentHTML - the menu markup (onclick strings preserved verbatim)
	 * @param {object} [pOptions] - passed through to PSM dropdown (align, position, className, maxWidth, ...)
	 * @returns {Promise<*>|false}
	 */
	spawnAnchoredMenu(pAnchor, pContentHTML, pOptions)
	{
		const tmpModal = this._getModal();
		if (!tmpModal)
		{
			return false;
		}
		if (this.fable.CSSMap && typeof this.fable.CSSMap.injectCSS === 'function')
		{
			this.fable.CSSMap.injectCSS();
		}
		const tmpOptions = Object.assign({ className: 'headlight-modal-menu' }, pOptions || {});
		tmpOptions.ContentHTML = pContentHTML;
		return tmpModal.dropdown(pAnchor, tmpOptions);
	}

	/**
	 * Dismiss the open anchored menu / dropdown (e.g. to toggle it closed from its
	 * own trigger button). Modals-only — never touches toasts/tooltips.
	 * @returns {void}
	 */
	dismissAnchoredMenu()
	{
		const tmpModal = this.fable.views && (this.fable.views['Pict-Section-Modal'] || this.fable.views['PictSectionModal']);
		if (tmpModal && typeof tmpModal.dismissDropdowns === 'function')
		{
			tmpModal.dismissDropdowns();
		}
	}

	/**
	 * Shared spawn path for spawnModal / spawnMiniModal.
	 * @param {object} pConfig - fully merged config
	 * @param {function} [pConfirmCallback]
	 * @param {function} [pCancelCallback]
	 * @param {object} pContext - { mini: boolean }
	 * @private
	 */
	_spawn(pConfig, pConfirmCallback, pCancelCallback, pContext)
	{
		this.configActive = pConfig;

		// Singleton: dismiss any existing modal first.
		this.dismissModals();

		// Wire callbacks (kept as instance methods so embedded onclick strings resolve).
		if (pConfirmCallback)
		{
			this.confirmCallback = async () =>
			{
				try
				{
					await pConfirmCallback();
				}
				catch (e)
				{
					console.error('Error in modal Confirm callback: ', e);
				}
				this.dismissModals();
			};
		}
		if (pCancelCallback)
		{
			this.cancelCallback = async () =>
			{
				try
				{
					await pCancelCallback();
				}
				catch (e)
				{
					console.error('Error in modal Cancel callback: ', e);
				}
				this.dismissModals();
			};
		}

		const tmpModal = this._getModal();
		if (!tmpModal)
		{
			return;
		}

		// PSM's view CSS is registered but only injected on render; it never renders
		// through the normal cycle, so inject explicitly (mirrors Headlight-Popover).
		if (this.fable.CSSMap && typeof this.fable.CSSMap.injectCSS === 'function')
		{
			this.fable.CSSMap.injectCSS();
		}

		const tmpPlan = this._adapter.translate(pConfig, pContext);

		const tmpShowOptions =
		{
			title: tmpPlan.title,
			content: tmpPlan.content,
			// Footer buttons are Bulma .button elements inside `content` (so FontAwesome
			// icons + the onclick->confirmCallback/cancelCallback coupling survive); PSM's
			// own button array stays empty.
			buttons: [],
			closeable: tmpPlan.closeable,
			onOpen: (pDialog) => { this._onDialogOpen(pDialog, tmpPlan, pConfig); },
			onClose: () => { this._onDialogClose(); },
		};
		if (tmpPlan.width)
		{
			tmpShowOptions.width = tmpPlan.width;
		}

		// Freeze background scroll (unless the caller opted out) BEFORE showing.
		if (tmpPlan.freezeScroll)
		{
			this.freezeBodyScroll();
		}

		tmpModal.show(tmpShowOptions);
	}

	/**
	 * Post-open DOM work PSM can't do from a string `content`: stamp our classes, append
	 * any live-element templates, inject custom CSS, position a mini modal, and restore the
	 * legacy autofocus (PSM focuses the first button; forms want the first input).
	 * @private
	 */
	_onDialogOpen(pDialog, pPlan, pConfig)
	{
		this._activeDialog = pDialog;

		// Classes (z-index for panels is handled by psm-modal.scss via the body navbar
		// class; these drive the wide/mini/brand looks).
		if (pPlan.className)
		{
			let tmpClasses = pPlan.className.split(/\s+/);
			for (let i = 0; i < tmpClasses.length; i++)
			{
				if (tmpClasses[i]) { pDialog.classList.add(tmpClasses[i]); }
			}
		}

		let tmpBody = pDialog.querySelector('.pict-modal-dialog-body');

		// Live-element templates (rare — most templates are HTML strings handled in content).
		if (tmpBody)
		{
			if (pPlan.cardElement) { this._appendElement(tmpBody, pConfig.modalCardTemplate); }
			if (pPlan.headerElement) { this._appendElement(tmpBody, pConfig.modalHeaderTemplate); }
			if (pPlan.bodyElement) { this._appendElement(tmpBody, pConfig.modalBodyTemplate); }
			if (pPlan.footerElement) { this._appendElement(tmpBody, pConfig.modalFooterTemplate); }
		}

		// Default footer as a real .pict-modal-dialog-footer band — a sibling of the body,
		// so the buttons sit below the (scrollable) body instead of overlapping its content.
		if (pPlan.footerContent)
		{
			let tmpFooter = document.createElement('div');
			tmpFooter.className = 'pict-modal-dialog-footer';
			tmpFooter.innerHTML = pPlan.footerContent;
			pDialog.appendChild(tmpFooter);
		}

		// Custom CSS as a <style> (textContent, never parsed as HTML — no injection surface).
		if (pPlan.customCSS)
		{
			let tmpStyle = document.createElement('style');
			tmpStyle.textContent = pPlan.customCSS;
			pDialog.appendChild(tmpStyle);
		}

		// Mini modal: anchor top-right below the navbar. Inline styles beat PSM's centering.
		// Drop the transform transition so it fades in place instead of sliding in from the
		// centered origin (which read as a top-left slide).
		if (pPlan.topOffset)
		{
			pDialog.style.position = 'fixed';
			pDialog.style.top = pPlan.topOffset;
			pDialog.style.right = '8px';
			pDialog.style.left = 'auto';
			pDialog.style.bottom = 'auto';
			pDialog.style.transform = 'none';
			pDialog.style.margin = '0';
			pDialog.style.transition = 'opacity var(--pict-modal-transition-duration, 0.18s) ease';
		}

		// Restore legacy autofocus: first visible, enabled input/textarea in the body.
		if (pPlan.focusFirstInput && tmpBody)
		{
			let tmpInput = tmpBody.querySelector('input:not([type="hidden"]):not([disabled]), textarea:not([disabled])');
			if (tmpInput)
			{
				tmpInput.focus();
				if (typeof tmpInput.select === 'function') { tmpInput.select(); }
			}
		}
	}

	/**
	 * PSM fired onClose (button click routed through our callbacks, or ×/Escape/overlay
	 * dismiss). PSM has already removed the dialog; reset our state. User callbacks are NOT
	 * run here — the footer buttons run them directly; ×/Escape/overlay are a plain dismiss
	 * (parity with the legacy background-click).
	 * @private
	 */
	_onDialogClose()
	{
		this._activeDialog = null;
		this._resetAfterClose();
	}

	/**
	 * Reset callbacks to their dismiss-only defaults and release the scroll lock.
	 * Idempotent.
	 * @private
	 */
	_resetAfterClose()
	{
		this.confirmCallback = () =>
		{
			this.dismissModals();
		};
		this.cancelCallback = () =>
		{
			this.dismissModals();
		};
		if (typeof document !== 'undefined' && document.body && document.body.style.position === 'fixed')
		{
			this.unfreezeBodyScroll();
		}
	}

	/**
	 * Append a live DOM element (nodeType 1) into a parent. String templates never reach
	 * here — the adapter folds those into `content`.
	 * @private
	 */
	_appendElement(pParent, pContent)
	{
		if (pContent && typeof pContent === 'object' && pContent.nodeType === 1)
		{
			pParent.appendChild(pContent);
		}
	}

	addLoader(message)
	{
		if (!this.loaderActive)
		{
			this.loaderActive = true;

			const tmpLoader = document.createElement('div');
			tmpLoader.id = 'modal-service-loader';
			tmpLoader.className = 'modal is-active';

			const tmpBg = document.createElement('div');
			tmpBg.className = 'modal-background delayed-fade-in-bg';
			tmpLoader.appendChild(tmpBg);

			const tmpContent = document.createElement('div');
			tmpContent.className = 'modal-content';
			tmpContent.style.backgroundColor = 'rgba(1, 1, 1, 0)';

			const tmpSplash = document.createElement('div');
			tmpSplash.className = 'is-loading-splash delayed-fade-in';
			tmpSplash.style.zIndex = '25';
			tmpSplash.style.backgroundColor = 'rgba(1, 1, 1, 0)';

			const tmpTitle = document.createElement('h1');
			tmpTitle.className = 'title';
			tmpTitle.textContent = message || 'Loading...';

			tmpSplash.appendChild(tmpTitle);
			tmpContent.appendChild(tmpSplash);
			tmpLoader.appendChild(tmpContent);
			document.body.appendChild(tmpLoader);
		}
	}

	dismissLoader()
	{
		document.querySelectorAll('#modal-service-loader')?.forEach?.(l => l.remove());
		this.loaderActive = false;
	}

	/**
	* Dismiss the currently open modal (singleton). Dismisses the PSM dialog — whose
	* onClose resets our state — and falls back to a direct reset when no dialog is tracked.
	* @return {void}
	*/
	dismissModals()
	{
		const tmpDialog = this._activeDialog;
		this._activeDialog = null;

		if (tmpDialog && typeof tmpDialog._dismiss === 'function' && !tmpDialog._dismissed)
		{
			// PSM removes the node and fires onClose -> _onDialogClose -> _resetAfterClose.
			tmpDialog._dismiss(null);
			return;
		}

		// No live dialog to dismiss (or already dismissed): reset directly.
		this._resetAfterClose();
	}

	/**
	* Fix the body background so that it's not scrollable when a modal is open.
	* @return {void}
	*/
	freezeBodyScroll()
	{
		this.lastScrollPosition = window.scrollY;
		document.body.style.position = 'fixed';
		document.body.style.top = `-${this.lastScrollPosition}px`;
		document.body.style.overflow = 'hidden';
		document.body.style.width = '100%';
	}

	/**
	* Reset the body background so that it's scrollable when a modal is closed.
	* @return {void}
	*/
	unfreezeBodyScroll()
	{
		const tmpScrollPosition = this.lastScrollPosition;
		document.body.style.position = '';
		document.body.style.top = '';
		//this is required to ensure that sticky headers stick
		document.body.style.overflow = 'visible';
		document.body.style.width = '';
		window.scrollTo(0, tmpScrollPosition);
		// reset for next time
		this.lastScrollPosition = 0;
	}
}

module.exports = HeadlightModalFableService;
module.exports.default_configuration = {};
// Re-export so callers can `spawnMiniModal({ ...HeadlightModal.MiniModalPreset, message })`.
module.exports.MiniModalPreset = libHeadlightModalAdapter.MiniModalPreset;
